// src/components/logs/SyncDetailsModal.jsx
import React from 'react';
import { formatDateTime, formatDuration, formatSyncType } from '../../utils/formatUtils';

/* ── Helpers ─────────────────────────────────────────────── */

const statusColor = (status) => {
  switch (status) {
    case 'completed': return 'var(--accent-green)';
    case 'failed':    return 'var(--accent-red)';
    case 'running':   return 'var(--accent-blue)';
    default:          return 'var(--text-muted)';
  }
};

const statusIcon = (status) => {
  switch (status) {
    case 'completed': return 'bi-check-circle-fill';
    case 'failed':    return 'bi-x-circle-fill';
    case 'running':   return 'bi-arrow-repeat';
    default:          return 'bi-question-circle';
  }
};

const formatResults = (results) => {
  if (!results || typeof results !== 'object') return null;
  return Object.entries(results).map(([key, val]) => {
    let display;
    if (val === null || val === undefined) {
      display = '—';
    } else if (typeof val === 'object') {
      display = JSON.stringify(val);
    } else {
      display = String(val);
    }
    return { key, display };
  });
};

/* ── Component ───────────────────────────────────────────── */

const SyncDetailsModal = ({ sync, onClose }) => {
  if (!sync) return null;

  const results = formatResults(sync.results);
  const logs = sync.logs || sync.activityLog || [];
  const hasError = sync.error || sync.errorMessage;

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div
        className="schedule-modal schedule-modal--lg"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="schedule-modal-header">
          <h2 className="schedule-modal-title">
            <i
              className={`bi ${statusIcon(sync.status)}`}
              style={{ color: statusColor(sync.status) }}
              aria-hidden="true"
            />
            {formatSyncType(sync.type)}
          </h2>
          <button className="schedule-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="schedule-modal-body" style={{ overflow: 'auto', flex: 1 }}>

          {/* Status + ID row */}
          <div className="sync-detail-meta-row">
            <div className="sync-detail-meta">
              <span className="sync-detail-meta-label">Status</span>
              <span
                className="sync-detail-status-badge"
                style={{
                  color: statusColor(sync.status),
                  backgroundColor: `color-mix(in srgb, ${statusColor(sync.status)} 12%, transparent)`,
                }}
              >
                <i className={`bi ${statusIcon(sync.status)}`} aria-hidden="true" />
                {sync.status || 'unknown'}
              </span>
            </div>

            <div className="sync-detail-meta">
              <span className="sync-detail-meta-label">ID</span>
              <code className="sync-detail-id">{sync.id}</code>
            </div>
          </div>

          {/* Times */}
          <div className="sync-detail-meta-row">
            <div className="sync-detail-meta">
              <span className="sync-detail-meta-label">Started</span>
              <span className="sync-detail-meta-value">{formatDateTime(sync.startTime)}</span>
            </div>
            <div className="sync-detail-meta">
              <span className="sync-detail-meta-label">Ended</span>
              <span className="sync-detail-meta-value">{sync.endTime ? formatDateTime(sync.endTime) : '—'}</span>
            </div>
            <div className="sync-detail-meta">
              <span className="sync-detail-meta-label">Duration</span>
              <span className="sync-detail-meta-value">{formatDuration(sync)}</span>
            </div>
          </div>

          {/* Progress */}
          {sync.progress != null && (
            <div className="sync-detail-section">
              <span className="sync-detail-section-title">Progress</span>
              {typeof sync.progress === 'number' ? (
                <>
                  <div className="sync-detail-progress-track">
                    <div
                      className="sync-detail-progress-fill"
                      style={{ width: `${Math.min(sync.progress, 100)}%` }}
                    />
                  </div>
                  <span className="sync-detail-progress-pct">{sync.progress}%</span>
                </>
              ) : typeof sync.progress === 'object' ? (
                <div className="sync-detail-results-grid">
                  {Object.entries(sync.progress).map(([k, v]) => (
                    <div key={k} className="sync-detail-result-item">
                      <span className="sync-detail-result-key">{k}</span>
                      <span className="sync-detail-result-val">
                        {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="sync-detail-meta-value">{String(sync.progress)}</span>
              )}
            </div>
          )}

          {/* Error */}
          {hasError && (
            <div className="sync-detail-section">
              <span className="sync-detail-section-title" style={{ color: 'var(--accent-red)' }}>
                <i className="bi bi-exclamation-triangle" aria-hidden="true" /> Error
              </span>
              <pre className="sync-detail-error">
                {typeof (sync.error || sync.errorMessage) === 'object'
                  ? JSON.stringify(sync.error || sync.errorMessage, null, 2)
                  : sync.error || sync.errorMessage}
              </pre>
            </div>
          )}

          {/* Results */}
          {results && results.length > 0 && (
            <div className="sync-detail-section">
              <span className="sync-detail-section-title">Results</span>
              <div className="sync-detail-results-grid">
                {results.map(({ key, display }) => (
                  <div key={key} className="sync-detail-result-item">
                    <span className="sync-detail-result-key">{key}</span>
                    <span className="sync-detail-result-val">{display}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Log */}
          {logs.length > 0 && (
            <div className="sync-detail-section">
              <span className="sync-detail-section-title">
                Activity Log ({logs.length} entries)
              </span>
              <div className="sync-detail-log-list">
                {logs.map((entry, i) => {
                  const time = entry.timestamp || entry.time;
                  const rawMsg = entry.message || entry.msg || entry;
                  const msg = typeof rawMsg === 'object' ? JSON.stringify(rawMsg) : String(rawMsg);
                  return (
                    <div key={i} className="sync-detail-log-entry">
                      {time && <span className="sync-detail-log-time">{new Date(time).toLocaleTimeString()}</span>}
                      <span className="sync-detail-log-msg">{msg}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Raw JSON toggle */}
          <details className="sync-detail-raw">
            <summary className="sync-detail-raw-toggle">
              <i className="bi bi-code-slash" aria-hidden="true" /> Raw JSON
            </summary>
            <pre className="sync-detail-raw-json">{JSON.stringify(sync, null, 2)}</pre>
          </details>
        </div>

        {/* Footer */}
        <div className="schedule-modal-footer">
          <button className="qa-btn qa-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default SyncDetailsModal;
